/* eslint-disable camelcase */
import { makeStyles } from '@material-ui/core';
import { Box } from '@mui/material';
import { Tag } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { deleteProjectAction, setCurrentProject } from '../../actions/ProjectAction';
import CButton from '../../component/Button';
import Copy from '../../component/Copy';
import ProjectForm from '../../component/forms/ProjectForm';
import InnerHeader from '../../component/InnerHeader';
import Modal from '../../component/Modal/Modal';
import NoxTable from '../../component/Table';
import useToggle from '../../hooks/useToggle';
import { useProjects } from '../../reactQuery/projects/queries/useProjects';

const useStyles = makeStyles({
  deleteButton: {
    '&.ant-tag:hover': {
      background: 'red',
      border: 'none',
      color: 'white'
    }
  }
});

const transformData = (projects) => {
  if (!projects || !projects.length) {
    return [];
  }

  return projects.map(({ _id, name, api_key, users }) => {
    return {
      _id,
      api_key,
      key: _id,
      name,
      status: 'Active',
      users: users?.length || 0
    };
  });
};

const Projects = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const { handleOpen, handleClose, open } = useToggle();
  const { data, isLoading, isFetching, refetch } = useProjects();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState({});

  const currentProject = useSelector(({ project }) => project.currentProject);
  const projects = useSelector(({ project }) => project.projects);

  const onViewClick = useCallback(
    (item) => {
      history.push(`/projects/${item._id}`, {
        projectId: item._id
      });
    },
    [history]
  );

  function selectProject(item) {
    const selectedProject = data.filter((project) => project._id === item._id);
    dispatch(setCurrentProject(selectedProject[0]));
    history.push('/content-model');
  }

  function confirmProjectDeletion(project) {
    setSelectedProject(project);
    setIsModalOpen(true);
  }

  function deleteProject() {
    dispatch(deleteProjectAction(selectedProject._id));
    setSelectedProject({});
    setIsModalOpen(false);
  }

  useEffect(() => {
    refetch();
  }, [projects]);

  const columns = useMemo(
    () => [
      {
        dataIndex: '_id',
        key: '_id',
        render: (id) => {
          return <Copy text={id} />;
        },
        sorter: (a, b) => a._id.toString().localeCompare(b._id),
        title: 'ID'
      },
      {
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.toString().localeCompare(b.name),
        title: 'Name'
      },
      {
        dataIndex: 'api_key',
        key: 'api_key',
        render: (apiKey) => {
          return <Copy text={apiKey} />;
        },
        sorter: (a, b) => a.api_key.toString().localeCompare(b.api_key),
        title: 'API Key',
        width: '35%'
      },
      {
        dataIndex: 'users',
        key: 'users',
        sorter: (a, b) => a.users - b.users,
        title: 'Number of Users'
      },
      {
        dataIndex: 'status',
        key: 'status',
        sorter: (a, b) => a.status.toString().localeCompare(b.status),
        title: 'Status'
      },
      {
        key: 'view',
        render: (item) => <Tag onClick={() => onViewClick(item)}>View</Tag>
      },
      {
        key: 'select',
        render: (item) => <Tag onClick={() => selectProject(item)}>Select</Tag>
      },
      {
        key: 'deleteProject',
        render: (project) => (
          <Tag className={classes.deleteButton} onClick={() => confirmProjectDeletion(project)}>
            Delete
          </Tag>
        )
      }
    ],
    [onViewClick, data]
  );

  const actions = [
    {
      onClick: handleOpen,
      title: 'Create Project',
      type: 'primary'
    }
  ];

  const transformedData = useMemo(() => transformData(data), [data]);
  const isDeleteProjectButtonDisabled = selectedProject?._id === currentProject?._id;

  return (
    <Box sx={currentProject ? { padding: '4rem 1rem' } : { padding: '2rem 4.18rem 4rem' }}>
      <InnerHeader title="All Projects" />
      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={transformedData}
        loading={isLoading || isFetching}
      />
      <ProjectForm handleClose={handleClose} open={open} />
      <Modal
        footerActions={
          <>
            <CButton
              onClick={() => {
                setSelectedProject({});
                setIsModalOpen(false);
              }}
              type="secondary"
              xstyle={{ height: '40px' }}
            >
              Cancel
            </CButton>
            <CButton
              className={`modal-delete-button ${isDeleteProjectButtonDisabled && 'disabled'}`}
              disabled={isDeleteProjectButtonDisabled}
              onClick={deleteProject}
              type="primary"
            >
              Delete
            </CButton>
          </>
        }
        handleClose={() => {
          setSelectedProject({});
          setIsModalOpen(false);
        }}
        isModalOpen={isModalOpen}
        styles={{
          '& .MuiDialogContent-root': {
            paddingTop: '5px'
          },
          '& .modal-delete-button': {
            '&.disabled': {
              background: 'rgba(0, 0, 0, 0.2) !important',
              color: 'rgba(0, 0, 0, 0.26) !important'
            },
            background: 'red !important',
            border: 'none',
            height: '40px !important'
          },
          width: '400px'
        }}
        titleContent={<h4 className="modal-title">Delete Project</h4>}
      >
        <p>
          Are you sure you want to delete <strong>{selectedProject.name}</strong> project?
        </p>
      </Modal>
    </Box>
  );
};

export default Projects;
